import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { Child, ChildSelection } from "../types/child";
// import { fireDataRefresh } from "../utils/events"; // Unused - kept for potential future use

interface ChildSelectionProps {
  user: any;
  darkMode: boolean;
  onSelectionChange: (selectedChildren: ChildSelection[]) => void;
}

const ChildSelectionComponent: React.FC<ChildSelectionProps> = ({
  user,
  darkMode,
  onSelectionChange,
}) => {
  const [children, setChildren] = useState<Child[]>([]);
  const [childSelections, setChildSelections] = useState<ChildSelection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChildren();
  }, [user]);

  // Listen for child-related data refresh events
  useEffect(() => {
    const handleDataRefresh = (event: CustomEvent) => {
      const reason = event.detail?.reason;
      if (
        reason === "child_added" ||
        reason === "child_updated" ||
        reason === "child_deleted"
      ) {
        fetchChildren();
      }
    };

    // Add event listener
    window.addEventListener("dataRefresh", handleDataRefresh as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener(
        "dataRefresh",
        handleDataRefresh as EventListener
      );
    };
  }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);

      const { data: childrenData, error } = await supabase
        .from("children")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      const childrenList = childrenData || [];
      setChildren(childrenList);

      // Initialize selections - start with all children selected
      const initialSelections: ChildSelection[] = childrenList.map((child) => ({
        child_id: child.id,
        name: child.name,
        selected: true,
      }));

      setChildSelections(initialSelections);
      onSelectionChange(initialSelections);
    } catch (error) {
      console.error("Error fetching children:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChildToggle = (childId: string) => {
    const updatedSelections = childSelections.map((child) => ({
      ...child,
      selected: child.child_id === childId ? !child.selected : child.selected,
    }));

    setChildSelections(updatedSelections);
    onSelectionChange(updatedSelections);
  };

  const handleSelectAll = () => {
    const updatedSelections = childSelections.map((child) => ({
      ...child,
      selected: true,
    }));

    setChildSelections(updatedSelections);
    onSelectionChange(updatedSelections);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-dark-blue"></div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div
        className={`text-center py-4 ${
          darkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        No children added. Please add children in settings first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Actions */}
      <div className="flex justify-end">
        <button
          onClick={handleSelectAll}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
            darkMode
              ? "bg-brand-dark-blue text-white hover:bg-brand-cyan shadow-md hover:shadow-lg"
              : "bg-brand-dark-blue text-white hover:bg-brand-cyan shadow-md hover:shadow-lg"
          }`}
        >
          Select All
        </button>
      </div>

      {/* Children List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {childSelections.map((child) => (
          <div
            key={child.child_id}
            onClick={() => handleChildToggle(child.child_id)}
            className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${
              child.selected
                ? darkMode
                  ? "bg-brand-dark-blue bg-opacity-30 border-brand-cyan shadow-lg shadow-brand-cyan/20"
                  : "bg-brand-dark-blue bg-opacity-20 border-brand-cyan shadow-lg shadow-brand-cyan/20"
                : darkMode
                ? "bg-gray-800 border-gray-600 hover:bg-gray-700 hover:border-gray-500"
                : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {/* Selection indicator in top-right corner */}
            {child.selected && (
              <div className="absolute top-3 right-3">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    darkMode ? "bg-brand-cyan" : "bg-brand-cyan"
                  }`}
                >
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Child name centered in the box */}
            <div className="flex items-center justify-center h-full min-h-[80px]">
              <span
                className={`text-xl font-bold text-center ${
                  child.selected
                    ? darkMode
                      ? "text-white"
                      : "text-brand-dark-blue"
                    : darkMode
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                {child.name}
              </span>
            </div>

            {/* Bottom indicator bar */}
            <div
              className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-xl ${
                child.selected
                  ? darkMode
                    ? "bg-brand-cyan"
                    : "bg-brand-cyan"
                  : "bg-transparent"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChildSelectionComponent;
